import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ResourceCard } from "@/components/resources/ResourceCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Library, Sparkles, Film, Download, Heart, Star, Settings2, FolderOpen, X } from "lucide-react";

const VIEWS = {
  new_month: { label: "New This Month", empty: "Nothing new has been added this month yet." },
  videos: { label: "Videos", empty: "There are no videos in the library yet." },
  downloaded_month: { label: "Downloaded This Month", empty: "No resources have been downloaded this month yet." },
};

function StatCard({ icon: Icon, label, value, tint, active, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={`rounded-2xl border bg-[#141414] p-5 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-white/[0.16] hover:bg-[#181818] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E30613] ${active ? "border-[#E30613] bg-[#E30613]/[0.06] hover:border-[#E30613]" : "border-white/[0.06]"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></span>
        {active && <span className="rounded-full bg-[#E30613] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Active</span>}
      </div>
      <p className="mt-4 font-display text-3xl font-semibold text-white">{value ?? "—"}</p>
      <p className="mt-1 text-xs uppercase tracking-wide text-neutral-500">{label}</p>
    </button>
  );
}

export default function ResourceCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "super_admin";
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [fileType, setFileType] = useState("all");
  const [tab, setTab] = useState("all"); // all | featured | favorites
  const [view, setView] = useState(null); // null | new_month | videos | downloaded_month

  const resetFilters = () => { setQ(""); setCategory("all"); setFileType("all"); setTab("all"); setSort("newest"); };
  const pickView = (v) => { resetFilters(); setView((cur) => (v && cur !== v ? v : null)); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q.trim()) params.q = q.trim();
      if (category !== "all") params.category = category;
      if (fileType !== "all") params.file_type = fileType;
      if (tab === "featured") params.featured = true;
      if (tab === "favorites") params.favorites = true;
      if (view) params.view = view;
      const { data } = await api.get("/resources", { params });
      setRows(data);
    } catch (e) {} finally { setLoading(false); }
  }, [q, category, sort, fileType, tab, view]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/resources/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/resources/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const onFav = (id, fav) => {
    setRows((prev) => tab === "favorites" && !fav ? prev.filter((r) => r.id !== id) : prev.map((r) => r.id === id ? { ...r, is_favorite: fav } : r));
  };

  const featured = rows.filter((r) => r.featured);
  const showFeaturedSection = tab === "all" && !view && !q.trim() && category === "all" && featured.length > 0;
  const heading = view ? VIEWS[view].label : tab === "favorites" ? "Your Favorites" : tab === "featured" ? "Featured" : "All Resources";

  return (
    <DashboardLayout title="Dealer Resource Center">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Resource Library</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">Dealer Resource Center</h2>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">Everything you need to successfully promote and sell SGBINOXE products.</p>
        </div>
        {isAdmin && (
          <button onClick={() => navigate("/admin/resources")} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="manage-resources-btn"><Settings2 className="h-4 w-4" /> Manage Resources</button>
        )}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Library} label="Total Resources" value={stats?.total_resources} tint="bg-[#E30613]/10 text-[#FF6069]" active={!view && tab === "all" && !q.trim() && category === "all" && fileType === "all"} onClick={() => pickView(null)} />
        <StatCard icon={Sparkles} label="New This Month" value={stats?.new_this_month} tint="bg-emerald-500/10 text-emerald-400" active={view === "new_month"} onClick={() => pickView("new_month")} />
        <StatCard icon={Film} label="Videos" value={stats?.videos} tint="bg-purple-500/10 text-purple-400" active={view === "videos"} onClick={() => pickView("videos")} />
        <StatCard icon={Download} label="Downloads This Month" value={stats?.downloads_this_month} tint="bg-sky-500/10 text-sky-400" active={view === "downloaded_month"} onClick={() => pickView("downloaded_month")} />
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[{ k: "all", label: "All", icon: Library }, { k: "featured", label: "Featured", icon: Star }, { k: "favorites", label: "Favorites", icon: Heart }].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => { setTab(k); setView(null); }} className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === k && !view ? "border-[#E30613] bg-[#E30613]/15 text-white" : "border-white/[0.1] text-neutral-400 hover:bg-white/[0.05]"}`} data-testid={`tab-${k}`}><Icon className="h-3.5 w-3.5" /> {label}</button>
        ))}
        {view && (
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E30613] bg-[#E30613]/15 px-3 py-1.5 text-sm text-white" data-testid="active-view-chip">
            {VIEWS[view].label}
            <button onClick={() => pickView(null)} className="text-neutral-300 hover:text-white" aria-label="Show all resources" data-testid="clear-view-btn"><X className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources by title, description, category or tag…" className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-10 text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]" data-testid="resource-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white lg:w-52" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="max-h-72 border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fileType} onValueChange={setFileType}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white lg:w-40" data-testid="filter-filetype"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
            <SelectItem value="zip">ZIP</SelectItem>
            <SelectItem value="png">Image</SelectItem>
            <SelectItem value="mp4">Video</SelectItem>
            <SelectItem value="link">Link</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white lg:w-48" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="most_downloaded">Most Downloaded</SelectItem>
            <SelectItem value="recently_updated">Recently Updated</SelectItem>
            <SelectItem value="featured">Featured First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/[0.06] bg-[#141414] py-24 text-center" data-testid="resources-empty">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.04] text-neutral-500">
            {tab === "favorites" ? <Heart className="h-7 w-7" /> : <FolderOpen className="h-7 w-7" />}
          </span>
          <p className="mt-4 text-base font-medium text-white">{tab === "favorites" ? "No favorites yet" : "No resources found"}</p>
          <p className="mt-1 max-w-sm text-sm text-neutral-500">{view && !q.trim() && category === "all" && fileType === "all" ? VIEWS[view].empty : tab === "favorites" ? "Tap the heart on any resource to save it here for quick access." : "Try adjusting your search or filters. New materials are added by SGBINOXE regularly."}</p>
          {(view || tab !== "all") && <button onClick={() => pickView(null)} className="mt-4 rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-200 hover:bg-white/[0.05]" data-testid="empty-show-all-btn">Show all resources</button>}
        </div>
      ) : (
        <>
          {showFeaturedSection && (
            <div className="mb-8">
              <div className="mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-[#E30613]" /><h3 className="font-display text-lg font-semibold text-white">Featured Resources</h3></div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {featured.slice(0, 3).map((r) => <ResourceCard key={r.id} resource={r} onFavoriteChange={onFav} />)}
              </div>
            </div>
          )}
          <div className="mb-3 flex items-center gap-2"><Library className="h-4 w-4 text-neutral-400" /><h3 className="font-display text-lg font-semibold text-white" data-testid="results-heading">{heading}</h3><span className="text-sm text-neutral-500">({rows.length})</span></div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {rows.map((r) => <ResourceCard key={r.id} resource={r} onFavoriteChange={onFav} />)}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
